<template>
  <div class="page-content">
    <div class="page-header">
      <div>
        <BreadcrumbComponent :items="breadcrumbs" />
        <h1 class="page-title" v-if="statement.period_label">{{ customer.full_name }} — {{ statement.period_label }}</h1>
      </div>
      <div class="d-flex" style="gap: 8px;" v-if="enable">
        <v-btn color="primary" depressed small @click="paymentDialog = true">
          <v-icon left small>mdi-cash-plus</v-icon>
          Enregistrer Paiement
        </v-btn>
        <v-btn color="primary" outlined small @click="openPdfFormatDialog">
          <v-icon left small>mdi-file-pdf-box</v-icon>
          PDF
        </v-btn>
      </div>
    </div>

    <PreloaderComponent :enable="enable" />

    <div v-if="enable">
      <v-card flat class="mb-3 pa-3 currency-toolbar">
        <p class="text-caption text--secondary mb-0">
          <strong>Vente ou paiement en HTG</strong> : montant uniquement dans la colonne <strong>HTG</strong> (la colonne <strong>USD</strong> reste « — », même si un taux est renseigné sur la ligne). <strong>En USD</strong> : montant uniquement dans la colonne <strong>USD</strong>, colonne <strong>HTG</strong> en « — ». Le solde cumulé HTG est la somme des mouvements en gourdes ; le <strong>solde USD</strong> cumule les montants en dollars des lignes USD et, pour les lignes HTG, l’équivalent <strong>HTG ÷ taux</strong> de la ligne (même règle que les colonnes débit/crédit convertibles). Si une ligne HTG n’a pas de taux, le solde USD ne peut pas être poursuivi (« — » sur cette ligne et les suivantes).
        </p>
      </v-card>

      <!-- Récapitulatif + switch devise -->
      <v-row class="mb-1 align-center flex-nowrap">
        <v-col cols="12" class="d-flex justify-end align-center py-1">
          <span class="text-body-2 text--secondary mr-2 d-none d-sm-inline">Totaux du relevé</span>
          <v-switch
            v-model="summaryUsdDisplay"
            inset
            dense
            hide-details
            class="mt-0 pt-0 summary-currency-switch"
            :label="summaryUsdDisplay ? 'USD' : 'HTG'"
          ></v-switch>
        </v-col>
      </v-row>
      <p v-if="summaryUsdDisplay && summaryUsdRecapIncomplete" class="text-caption warning--text mb-2 text-right">
        Au moins une vente n’a pas de taux : le total des ventes en dollars ne peut pas être calculé.
      </p>
      <v-row class="mb-2">
        <v-col cols="6" md="3">
          <v-card flat class="summary-card">
            <div class="summary-icon-sm" style="background:#194173"><v-icon color="white" size="18">mdi-calendar</v-icon></div>
            <div class="summary-val">{{ statement.period_label }}</div>
            <div class="summary-lbl">
              <v-chip x-small :color="getStatusColor(statement.status)" dark class="mt-1">{{ getStatusLabel(statement.status) }}</v-chip>
            </div>
          </v-card>
        </v-col>
        <v-col cols="6" md="3">
          <v-card flat class="summary-card">
            <div class="summary-icon-sm" style="background:#F44336"><v-icon color="white" size="18">mdi-cart</v-icon></div>
            <div class="summary-val error--text">{{ summarySalesDisplay }}</div>
            <div class="summary-lbl">Ventes ({{ summaryCurrencySuffix }})</div>
          </v-card>
        </v-col>
        <v-col cols="6" md="3">
          <v-card flat class="summary-card">
            <div class="summary-icon-sm" style="background:#4CAF50"><v-icon color="white" size="18">mdi-cash-check</v-icon></div>
            <div class="summary-val success--text">{{ summaryPaymentsDisplay }}</div>
            <div class="summary-lbl">Paiements ({{ summaryCurrencySuffix }})</div>
          </v-card>
        </v-col>
        <v-col cols="6" md="3">
          <v-card flat class="summary-card">
            <div class="summary-icon-sm" :style="'background:' + (statement.balance_htg > 0 ? '#F44336' : '#4CAF50')"><v-icon color="white" size="18">mdi-scale-balance</v-icon></div>
            <div class="summary-val" :class="statement.balance_htg > 0 ? 'error--text' : 'success--text'">{{ summaryBalanceDisplay }}</div>
            <div class="summary-lbl">Solde ({{ summaryCurrencySuffix }})</div>
          </v-card>
        </v-col>
      </v-row>

      <!-- Timeline -->
      <v-card flat class="table-card overflow-x-auto">
        <div class="d-flex align-center justify-space-between pa-3">
          <span class="text-subtitle-2 font-weight-bold">Historique des transactions</span>
          <span class="text-caption text--secondary ml-1">{{ timelineWithBalance.length }} opération{{ timelineWithBalance.length > 1 ? 's' : '' }}</span>
        </div>
        <v-simple-table dense class="statement-dual-table">
          <thead>
            <tr>
              <th class="text-left">DATE</th>
              <th class="text-center">TYPE</th>
              <th class="text-left">DESCRIPTION</th>
              <th class="text-right amt-col">DÉBIT (HTG)</th>
              <th class="text-right amt-col">DÉBIT (USD)</th>
              <th class="text-right amt-col">CRÉDIT (HTG)</th>
              <th class="text-right amt-col">CRÉDIT (USD)</th>
              <th class="text-right amt-col balance-sep">SOLDE (HTG)</th>
              <th class="text-right amt-col">SOLDE (USD)</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, idx) in timelineWithBalance" :key="idx">
              <td class="text-left text-caption">{{ item.date | formatDate }}</td>
              <td class="text-center">
                <v-chip x-small :color="item.type === 'sale' ? 'error' : 'success'" dark>
                  {{ item.type === 'sale' ? 'Vente' : 'Paiement' }}
                </v-chip>
              </td>
              <td class="text-left desc-cell">
                <span v-if="item.type === 'sale'">
                  <template v-if="lineCurrency(item) === 'USD' && ((item.original_amount != null && item.original_amount !== '') || item.exchange_rate)">
                    <span v-if="item.original_amount != null && item.original_amount !== ''">{{ item.original_amount | formatNumber }} USD</span>
                    <span v-if="item.exchange_rate">&nbsp;× {{ item.exchange_rate }}</span>
                    <span v-if="item.description" class="text-caption text--secondary desc-secondary">{{ item.description }}</span>
                  </template>
                  <template v-else>{{ item.description }}</template>
                </span>
                <span v-else>
                  <span v-if="lineCurrency(item) === 'USD' && ((item.original_amount != null && item.original_amount !== '') || item.exchange_rate)">
                    <span v-if="item.original_amount != null && item.original_amount !== ''">{{ item.original_amount | formatNumber }} USD</span>
                    <span v-if="item.exchange_rate">&nbsp;× {{ item.exchange_rate }}</span>
                    <span v-if="item.description" class="text-caption text--secondary desc-secondary">{{ item.description }}</span>
                  </span>
                  <span v-else>{{ item.description }}</span>
                  <span v-if="item.notes" class="text-caption text--secondary desc-notes"> — {{ item.notes }}</span>
                </span>
              </td>
              <td class="text-right amt-col">
                <span v-if="item.type === 'sale'" class="error--text font-weight-medium">{{ debitHtgCell(item) }}</span>
              </td>
              <td class="text-right amt-col">
                <span v-if="item.type === 'sale'" class="error--text font-weight-medium">{{ debitUsdCell(item) }}</span>
              </td>
              <td class="text-right amt-col">
                <span v-if="item.type === 'payment'" class="success--text font-weight-medium">{{ creditHtgCell(item) }}</span>
              </td>
              <td class="text-right amt-col">
                <span v-if="item.type === 'payment'" class="success--text font-weight-medium">{{ creditUsdCell(item) }}</span>
              </td>
              <td class="text-right amt-col balance-sep font-weight-bold" :class="item.running_balance > 0 ? 'error--text' : ''">{{ fmt(item.running_balance) }}</td>
              <td class="text-right amt-col font-weight-bold" :class="item.running_balance > 0 ? 'error--text' : ''">{{ fmtUsdCell(item.running_balance_usd_equiv) }}</td>
            </tr>
            <tr v-if="timelineWithBalance.length === 0">
              <td colspan="9" class="text-center text--secondary pa-6">Aucune opération pour cette période</td>
            </tr>
          </tbody>
        </v-simple-table>
      </v-card>
    </div>

    <!-- Payment dialog -->
    <v-dialog v-model="paymentDialog" max-width="480" @click:outside="closePaymentDialog">
      <v-card class="dialog-card">
        <v-card-title class="dialog-title">
          <v-icon color="success" size="18" class="mr-2">mdi-cash-plus</v-icon>
          Enregistrer un paiement
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="dialog-body">
          <v-alert type="info" text dense class="mb-4">
            Solde actuel: <strong>{{ fmt(statement.balance_htg) }} HTG</strong>
          </v-alert>

          <v-row>
            <v-col cols="7">
              <div class="form-group">
                <label class="form-label">Montant *</label>
                <v-text-field v-model.number="payment.amount" type="number" step="0.01" outlined dense hide-details="auto" :rules="[v => paymentAmountRule(v)]" placeholder="0.00"></v-text-field>
                <p class="text-caption text--secondary mt-1 mb-0">Montant négatif autorisé pour régulariser un solde (ex. dette antérieure).</p>
              </div>
            </v-col>
            <v-col cols="5">
              <div class="form-group">
                <label class="form-label">Devise</label>
                <v-select v-model="payment.currency" :items="['HTG', 'USD']" outlined dense hide-details></v-select>
              </div>
            </v-col>
          </v-row>

          <div class="form-group">
            <label class="form-label">Taux de change (1 USD = ? HTG) *</label>
            <v-text-field
              v-model.number="payment.exchange_rate"
              type="number"
              step="0.01"
              min="0"
              outlined
              dense
              hide-details="auto"
              :rules="[v => paymentExchangeRateRule(v)]"
              placeholder="135.00"
            ></v-text-field>
            <p class="text-caption text--secondary mt-1 mb-0">Taux du jour (1 USD = combien de gourdes). Obligatoire pour tout paiement, en gourdes comme en dollars.</p>
            <p v-if="payment.currency === 'USD' && paymentAmountNonZero && payment.exchange_rate > 0" class="text-caption success--text mt-1 mb-0">
              = {{ fmt(payment.amount * payment.exchange_rate) }} HTG
            </p>
            <p v-else-if="payment.currency === 'HTG' && paymentAmountNonZero && payment.exchange_rate > 0" class="text-caption success--text mt-1 mb-0">
              Équivalent indicatif : ≈ {{ fmtUsd(payment.amount / payment.exchange_rate) }} USD
            </p>
          </div>

          <div class="form-group">
            <label class="form-label">Référence</label>
            <v-text-field v-model="payment.reference_number" outlined dense hide-details placeholder="Optionnel"></v-text-field>
          </div>

          <div class="form-group">
            <label class="form-label">Notes</label>
            <v-textarea v-model="payment.notes" outlined dense rows="2" hide-details placeholder="Optionnel"></v-textarea>
          </div>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions class="dialog-actions">
          <v-spacer></v-spacer>
          <v-btn text @click="closePaymentDialog">Annuler</v-btn>
          <v-btn color="success" depressed :loading="paymentLoading" :disabled="paymentSubmitDisabled" @click="submitPayment">
            Enregistrer
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Choix du format PDF (HTG / USD / les deux) -->
    <v-dialog v-model="pdfFormatDialog" max-width="420" @click:outside="pdfFormatDialog = false">
      <v-card class="dialog-card">
        <v-card-title class="dialog-title">
          <v-icon color="error" size="20" class="mr-2">mdi-file-pdf-box</v-icon>
          Exporter le relevé en PDF
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="dialog-body">
          <p class="text-body-2 text--secondary mb-3">Choisissez comment les montants doivent apparaître sur le relevé PDF.</p>
          <v-radio-group v-model="pdfDisplayMode" dense hide-details class="mt-0">
            <v-radio value="HTG" class="mb-1">
              <template v-slot:label>
                <div>
                  <span class="font-weight-medium">HTG</span>
                  <span class="text-caption text--secondary d-block">Montants en gourdes uniquement.</span>
                </div>
              </template>
            </v-radio>
            <v-radio value="USD" class="mb-1">
              <template v-slot:label>
                <div>
                  <span class="font-weight-medium">USD</span>
                  <span class="text-caption text--secondary d-block">Montants en dollars, selon le taux de chaque opération.</span>
                </div>
              </template>
            </v-radio>
            <v-radio value="DUAL">
              <template v-slot:label>
                <div>
                  <span class="font-weight-medium">Les deux (DUAL)</span>
                  <span class="text-caption text--secondary d-block">Colonnes HTG et USD, comme à l’écran.</span>
                </div>
              </template>
            </v-radio>
          </v-radio-group>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions class="dialog-actions">
          <v-btn text @click="pdfFormatDialog = false">Annuler</v-btn>
          <v-spacer></v-spacer>
          <v-btn color="primary" depressed :loading="pdfLoading" @click="confirmDownloadPdf">
            Télécharger
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <FlashMessage :position="'right bottom'"></FlashMessage>
  </div>
</template>

<script>
import BreadcrumbComponent from '../components/BreadcrumbComponent.vue'
import PreloaderComponent from '../components/PreloaderComponent.vue'

export default {
  name: 'StatementDetailView',
  components: { BreadcrumbComponent, PreloaderComponent },
  data: () => ({
    enable: false,
    customerId: null,
    statementId: null,
    pdfLoading: false,
    pdfFormatDialog: false,
    pdfDisplayMode: 'DUAL',
    paymentDialog: false,
    paymentLoading: false,
    customer: { id: null, full_name: '', credit_limit: 0 },
    statement: { id: null, period_label: '', status: 'pending', total_sales_htg: 0, total_payments_htg: 0, balance_htg: 0 },
    timeline: [],
    payment: { amount: '', currency: 'HTG', exchange_rate: '', reference_number: '', notes: '' },
    /** Récapitulatif : false = HTG ; true = USD (ventes/paiements = sommes par ligne ; solde = dernière ligne du tableau). */
    summaryUsdDisplay: false,
    breadcrumbs: [
      { text: 'Clients', disabled: false, href: '/customers' },
      { text: 'Relevés', disabled: false, href: '#' },
      { text: 'Détails', disabled: true, href: '#' },
    ],
  }),
  computed: {
    /**
     * Solde HTG : cumul classique. Solde USD : cumul des mouvements en USD (vente +, paiement −),
     * chaque ligne au même taux que pour les colonnes débit/crédit (pas « tout le solde HTG ÷ taux courant »,
     * ce qui faussait les enchaînements du type « 6,06 USD puis paiement 50 USD »).
     */
    timelineWithBalance() {
      var self = this
      var balance = 0
      var runningUsd = 0
      var usdChainBroken = false
      return this.timeline.map(function (item) {
        if (item.type === 'sale') balance += parseFloat(item.amount_htg) || 0
        else balance -= parseFloat(item.amount_htg) || 0

        var equiv = null
        if (usdChainBroken) {
          equiv = null
        } else {
          var u = item.type === 'sale' ? self.saleUsdContribution(item) : self.paymentUsdContribution(item)
          if (u == null || Number.isNaN(u)) {
            usdChainBroken = true
            equiv = null
          } else if (item.type === 'sale') {
            runningUsd += u
            equiv = runningUsd
          } else {
            runningUsd -= u
            equiv = runningUsd
          }
        }
        return Object.assign({}, item, { running_balance: balance, running_balance_usd_equiv: equiv })
      })
    },
    paymentAmountNonZero() {
      var a = Number(this.payment.amount)
      return !Number.isNaN(a) && a !== 0
    },
    paymentSubmitDisabled() {
      if (this.paymentLoading) return true
      if (!this.paymentAmountNonZero) return true
      var r = Number(this.payment.exchange_rate)
      if (this.payment.exchange_rate === '' || this.payment.exchange_rate === null || Number.isNaN(r) || r <= 0) return true
      return false
    },
    /** Avertissement : ventes présentes mais aucune conversion USD possible (taux manquant sur une vente HTG). */
    summaryUsdRecapIncomplete() {
      return (
        this.summaryUsdDisplay &&
        this.timeline.some(function (i) { return i.type === 'sale' }) &&
        this.summarySalesUsdFromTimeline == null
      )
    },
    summaryCurrencySuffix() {
      return this.summaryUsdDisplay ? 'USD' : 'HTG'
    },
    /** Somme des ventes en USD : chaque ligne vente avec son taux (comme la colonne solde de la ligne). */
    summarySalesUsdFromTimeline() {
      var self = this
      var sales = this.timeline.filter(function (i) { return i.type === 'sale' })
      if (sales.length === 0) return 0
      var sum = 0
      for (var k = 0; k < sales.length; k++) {
        var u = self.saleUsdContribution(sales[k])
        if (u == null || Number.isNaN(u)) return null
        sum += u
      }
      return sum
    },
    /** Somme des paiements en USD (ligne à ligne : USD = original, HTG avec taux = HTG/taux). */
    summaryPaymentsUsdFromTimeline() {
      var self = this
      var sum = 0
      this.timeline.forEach(function (item) {
        if (item.type !== 'payment') return
        var u = self.paymentUsdContribution(item)
        if (u != null && !Number.isNaN(u)) sum += u
      })
      return sum
    },
    summarySalesDisplay() {
      if (!this.summaryUsdDisplay) return this.fmt(this.statement.total_sales_htg)
      var sum = this.summarySalesUsdFromTimeline
      if (sum == null) return '—'
      return this.fmtUsd(sum)
    },
    summaryPaymentsDisplay() {
      if (!this.summaryUsdDisplay) return this.fmt(this.statement.total_payments_htg)
      var sum = this.summaryPaymentsUsdFromTimeline
      return this.fmtUsd(sum)
    },
    /** Solde USD = dernière valeur de la colonne « SOLDE (USD) » (cumul des mouvements USD ligne à ligne). */
    summaryBalanceUsdFromTimeline() {
      var rows = this.timelineWithBalance
      if (!rows.length) return null
      var last = rows[rows.length - 1]
      return last.running_balance_usd_equiv
    },
    summaryBalanceDisplay() {
      if (!this.summaryUsdDisplay) return this.fmt(this.statement.balance_htg)
      var u = this.summaryBalanceUsdFromTimeline
      if (u == null) return '—'
      return this.fmtUsd(u)
    },
  },
  methods: {
    paymentAmountRule(v) {
      if (v === '' || v === null || v === undefined) return 'Requis'
      var n = Number(v)
      if (Number.isNaN(n)) return 'Nombre invalide'
      if (n === 0) return 'Le montant ne peut pas être zéro'
      return true
    },
    paymentExchangeRateRule(v) {
      if (v === '' || v === null || v === undefined) return 'Requis'
      var n = Number(v)
      if (Number.isNaN(n) || n <= 0) return 'Taux invalide (strictement > 0)'
      return true
    },
    fmt(v) {
      if (!v && v !== 0) return '0.00'
      var p = Math.abs(parseFloat(v)).toFixed(2).split('.')
      p[0] = p[0].replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
      return (parseFloat(v) < 0 ? '-' : '') + p.join('.')
    },
    fmtUsd(v) {
      if (v == null || v === '') return '—'
      var n = parseFloat(v)
      if (Number.isNaN(n)) return '—'
      var p = Math.abs(n).toFixed(2).split('.')
      p[0] = p[0].replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
      return (n < 0 ? '-' : '') + p.join('.')
    },
    /** Affichage cellule USD (null / NaN → tiret). */
    fmtUsdCell(v) {
      if (v == null || v === '') return '—'
      var n = parseFloat(v)
      if (Number.isNaN(n)) return '—'
      return this.fmtUsd(n)
    },
    /** USD pour une vente : devise USD → lineMovementUsd ; HTG avec taux → HTG/taux ; sinon null. */
    saleUsdContribution(item) {
      if (item.type !== 'sale') return null
      if (this.lineCurrency(item) === 'USD') return this.lineMovementUsd(item)
      var r = this.lineExchangeRate(item)
      if (r == null || r <= 0) return null
      var htg = parseFloat(item.amount_htg)
      if (Number.isNaN(htg)) return null
      return htg / r
    },
    /** USD pour un paiement : devise USD → lineMovementUsd ; HTG avec taux → HTG/taux ; sinon ignoré dans la somme. */
    paymentUsdContribution(item) {
      if (item.type !== 'payment') return null
      if (this.lineCurrency(item) === 'USD') return this.lineMovementUsd(item)
      var r = this.lineExchangeRate(item)
      if (r == null || r <= 0) return null
      var htg = parseFloat(item.amount_htg)
      if (Number.isNaN(htg)) return null
      return htg / r
    },
    /** Devise de la ligne timeline (API : vente ou paiement). */
    lineCurrency(item) {
      var c = item.currency != null && item.currency !== '' ? item.currency : 'HTG'
      return String(c).toUpperCase()
    },
    /** Taux HTG pour 1 USD sur la ligne (si présent et valide). */
    lineExchangeRate(item) {
      var r = parseFloat(item.exchange_rate)
      if (item.exchange_rate == null || item.exchange_rate === '') return null
      if (Number.isNaN(r) || r <= 0) return null
      return r
    },
    /**
     * Montant en USD pour colonnes DÉBIT/CRÉDIT (USD) : uniquement si la ligne est en devise USD.
     * Utilise original_amount si valide, sinon amount_htg / exchange_rate.
     * Les lignes en HTG ne renvoient jamais de valeur ici (pas d’équivalent dans la colonne USD).
     */
    lineMovementUsd(item) {
      if (this.lineCurrency(item) !== 'USD') return null
      var rate = this.lineExchangeRate(item)
      var htg = parseFloat(item.amount_htg)
      if (Number.isNaN(htg)) return null
      var oa = item.original_amount
      if (oa != null && oa !== '') {
        var u = parseFloat(oa)
        if (!Number.isNaN(u)) return u
      }
      if (rate == null) return null
      return htg / rate
    },
    /** Débit HTG : uniquement si la vente est en HTG (sinon « — », le montant USD est en colonne USD). */
    debitHtgCell(item) {
      if (item.type !== 'sale') return ''
      if (this.lineCurrency(item) === 'USD') return '—'
      return this.fmt(item.amount_htg)
    },
    /** Débit USD : uniquement si la vente est libellée en USD (pas d’équivalent depuis une vente HTG). */
    debitUsdCell(item) {
      if (item.type !== 'sale') return ''
      if (this.lineCurrency(item) !== 'USD') return '—'
      return this.fmtUsdCell(this.lineMovementUsd(item))
    },
    /** Crédit HTG : uniquement si le paiement est en HTG. */
    creditHtgCell(item) {
      if (item.type !== 'payment') return ''
      if (this.lineCurrency(item) === 'USD') return '—'
      return this.fmt(item.amount_htg)
    },
    /** Crédit USD : uniquement si le paiement est en USD (pas d’équivalent depuis un paiement HTG). */
    creditUsdCell(item) {
      if (item.type !== 'payment') return ''
      if (this.lineCurrency(item) !== 'USD') return '—'
      return this.fmtUsdCell(this.lineMovementUsd(item))
    },
    getStatusColor(status) {
      var map = { paid: 'success', due: 'error', partially_paid: 'warning', pending: 'info' }
      return map[status] || 'info'
    },
    getStatusLabel(status) {
      var map = { paid: 'Payé', due: 'Dû', partially_paid: 'Partiel', pending: 'En cours' }
      return map[status] || status
    },
    load() {
      var self = this
      this.$axios.get('/customers/' + this.customerId + '/statements/' + this.statementId)
        .then(function (r) {
          self.customer = r.data.customer
          self.statement = r.data.statement
          self.timeline = r.data.timeline
          self.breadcrumbs = [
            { text: 'Clients', disabled: false, href: '/customers' },
            { text: self.customer.full_name, disabled: false, href: '/customers/' + self.customerId + '/statements' },
            { text: self.statement.period_label, disabled: true, href: '#' },
          ]
          self.enable = true
        })
        .catch(function () { self.enable = true })
    },
    submitPayment() {
      var amt = parseFloat(this.payment.amount)
      if (this.payment.amount === '' || this.payment.amount === null || Number.isNaN(amt) || amt === 0) return
      var rate = parseFloat(this.payment.exchange_rate)
      if (!this.payment.exchange_rate && this.payment.exchange_rate !== 0) return
      if (Number.isNaN(rate) || rate <= 0) return

      var self = this
      this.paymentLoading = true
      var payload = {
        amount: parseFloat(this.payment.amount),
        currency: this.payment.currency,
        exchange_rate: rate,
        notes: this.payment.notes,
        reference_number: this.payment.reference_number,
      }

      this.$axios.post('/customers/' + this.customerId + '/statements/' + this.statementId + '/payments', payload)
        .then(function (r) {
          self.paymentLoading = false
          self.paymentDialog = false
          self.resetPayment()
          self.flashMessage.success({ title: 'Succès', message: 'Paiement enregistré' })
          if (r.data.statement) self.statement = r.data.statement
          self.load()
        })
        .catch(function (error) {
          self.paymentLoading = false
          var msg = "Erreur lors de l'enregistrement"
          if (error.response && error.response.data && error.response.data.message) msg = error.response.data.message
          self.flashMessage.error({ title: 'Erreur', message: msg })
        })
    },
    closePaymentDialog() { this.paymentDialog = false; this.resetPayment() },
    resetPayment() { this.payment = { amount: '', currency: 'HTG', exchange_rate: '', reference_number: '', notes: '' } },
    /** Query PDF : display_currency = HTG | USD | DUAL (choix utilisateur dans le modal). */
    pdfRequestParams() {
      return { display_currency: this.pdfDisplayMode }
    },
    openPdfFormatDialog() {
      this.pdfFormatDialog = true
    },
    confirmDownloadPdf() {
      this.pdfFormatDialog = false
      this.downloadPdf()
    },
    downloadPdf() {
      var self = this
      this.pdfLoading = true
      var url = '/customers/' + this.customerId + '/statements/' + this.statementId + '/pdf'
      this.$axios.get(url, { responseType: 'blob', params: this.pdfRequestParams() })
        .then(function (r) {
          var blob = new Blob([r.data], { type: 'application/pdf' })
          var link = document.createElement('a')
          link.href = window.URL.createObjectURL(blob)
          link.download = 'releve_' + self.customer.full_name.replace(/\s+/g, '_') + '_' + self.statement.period_label.replace(/\s+/g, '_') + '.pdf'
          link.click()
          window.URL.revokeObjectURL(link.href)
          self.pdfLoading = false
        })
        .catch(function () { self.pdfLoading = false })
    },
  },
  mounted() {
    this.customerId = this.$route.params.id
    this.statementId = this.$route.params.statementId
    this.load()
  },
}
</script>

<style scoped>
.summary-card { border: 1px solid #e8ecf1 !important; border-radius: 10px !important; box-shadow: 0 1px 3px rgba(0,0,0,0.04) !important; padding: 16px; text-align: center; }
.summary-icon-sm { width: 36px; height: 36px; border-radius: 8px; display: inline-flex; align-items: center; justify-content: center; margin-bottom: 8px; }
.summary-val { font-size: 1.125rem; font-weight: 700; color: #1a2332; }
.summary-lbl { font-size: 0.75rem; color: #64748b; margin-top: 2px; }
.table-card { border: 1px solid #e8ecf1 !important; border-radius: 8px !important; box-shadow: 0 1px 3px rgba(0,0,0,0.04) !important; overflow: hidden; }
.table-card .v-simple-table thead th { font-size: 0.6875rem !important; font-weight: 600; text-transform: uppercase; letter-spacing: 0.04em; color: #94a3b8 !important; background: transparent !important; border-bottom: 1px solid #e8ecf1 !important; padding: 8px 16px !important; }
.table-card .v-simple-table tbody td { font-size: 0.8125rem; padding: 10px 16px !important; border-bottom: 1px solid #f1f5f9 !important; }
.table-card .v-simple-table tbody tr:last-child td { border-bottom: none !important; }
.table-card .v-simple-table tbody tr:hover { background: rgba(25, 65, 115, 0.03) !important; }
.dialog-card { border-radius: 12px !important; }
.dialog-title { font-size: 0.9375rem !important; font-weight: 600 !important; padding: 14px 20px 12px !important; }
.dialog-body { padding: 20px 20px 8px !important; }
.dialog-actions { padding: 8px 16px 12px !important; }
.form-group { margin-bottom: 16px; }
.form-label { display: block; font-size: 0.75rem; font-weight: 600; color: #475569; margin-bottom: 4px; }
.currency-toolbar { border: 1px solid #e8ecf1 !important; border-radius: 10px !important; box-shadow: 0 1px 3px rgba(0,0,0,0.04) !important; }
.statement-dual-table .amt-col { white-space: nowrap; font-size: 0.8125rem; }
.statement-dual-table thead th.amt-col { padding-left: 6px !important; padding-right: 6px !important; }
.statement-dual-table .desc-cell .desc-secondary { display: inline-block; margin-top: 2px; margin-left: 2px; }
.statement-dual-table .desc-cell .desc-notes { display: inline-block; margin-left: 4px; }
/* Séparateur visuel entre mouvements (débit/crédit) et soldes cumulés */
.statement-dual-table thead th.balance-sep,
.statement-dual-table tbody td.balance-sep {
  border-left: 2px solid #cbd5e1;
  padding-left: 14px !important;
}
</style>
